import React, { useCallback, useContext, useMemo } from "react";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { Feature, LineString } from "geojson";
import rbush from "geojson-rbush";
import { passing_draw_polygon as passingDrawMode } from "mapbox-gl-draw-passing-mode";

import { DRAWING_CONSTANTS } from "fond/constants";
import DrawHandler from "fond/draw/DrawHandler";
import leadInDrawStyles from "fond/draw/modes/design/leadIns/leadInDrawStyles";
import simpleSelectLeadIns from "fond/draw/modes/design/leadIns/simpleSelectLeadIns";
import { MapContext } from "fond/map/MapProvider";
import { lineStringDrawingConfig } from "fond/map/Toolbar";
import { MuPowerEditableLayerIds } from "fond/types";

import directSelectLeadIns from "./leadIns/directSelectLeadIns";
import drawLeadIns from "./leadIns/drawLeadIns";
import { isValid, isValidTree } from "./leadIns/helper";

const modes = {
  simple_select: simpleSelectLeadIns,
  direct_select: directSelectLeadIns,
  draw_line_string: drawLeadIns,
  passing_draw_polygon: passingDrawMode,
};

interface MuPowerLeadInHandlerProps {
  initialFeatures: Feature<LineString>[];
  snapTargetFeatures: Feature[];
}

const MuPowerLeadInHandler: React.FC<MuPowerLeadInHandlerProps> = ({ initialFeatures, snapTargetFeatures }) => {
  const { map } = useContext(MapContext);

  const validatedFeatures: Feature<LineString>[] = useMemo(() => {
    const tree = rbush();
    tree.load(snapTargetFeatures);

    return initialFeatures.map((f) => ({
      ...f,
      properties: { ...f.properties, [DRAWING_CONSTANTS.Valid]: String(isValidTree(f, tree)) },
    }));
  }, [initialFeatures, snapTargetFeatures]);

  const snapGetFeatures = useCallback((_m: unknown, draw: MapboxDraw) => [...draw.getAll().features, ...snapTargetFeatures], [snapTargetFeatures]);

  const options: MapboxDrawOptions = useMemo(
    () =>
      ({
        snap: true,
        snapOptions: {
          snapPx: 25,
          snapToMidPoints: false,
          snapVertexPriorityDistance: 0.0025,
          snapGetFeatures,
        },
      }) as MapboxDrawOptions,
    [snapGetFeatures]
  );

  const getFeatureProperties = useCallback(
    (feature: Feature<LineString>) => ({
      layerId: MuPowerEditableLayerIds.leadins,
      [DRAWING_CONSTANTS.Valid]: map && isValid(feature, map).toString(),
    }),
    [map]
  );

  return (
    <DrawHandler
      startingMode={validatedFeatures.length > 0 ? "simple_select" : "draw_line_string"}
      initialFeatures={validatedFeatures}
      styles={leadInDrawStyles}
      config={lineStringDrawingConfig}
      getFeatureProperties={getFeatureProperties}
      options={options}
      modes={modes}
    />
  );
};

export default MuPowerLeadInHandler;
